import {
    Box,
    Checkbox,
    Flex,
} from "@chakra-ui/react";
import { InfoIcon } from "@root/assets/icons/info-icon-login";
import { KPSLogo } from "@root/assets/icons/kpslogo";

import {
    AuthFormContainer,
    AuthCardEllipse,
    AuthCardTopFrame,
    AuthHeading,
    AuthSubheading,
    AuthFieldsWrapper,
    AuthButton,
    AuthButtonWrapper,
    AuthCheckboxText,
} from "@root/commonStyles/commonStyle";
import { ROUTE_NAMES, USER_TYPES } from "@root/constants";
import { adminLogin } from "@store/thunks/admin/auth";
import { Field, Formik } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FloatingInput } from "./floating-input";
import { LanguageToggle } from "@root/components/dashboard/sidebar/languagetoggler";

export const SuperAdminLogin: React.FC = () => {
    const { t } = useTranslation("common");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();


    const initialValues = {
        email: "",
        password: "",
        rememberMe: false,
    };

    const validate = (values: LoginValues) => {
        const errors: Partial<LoginValues> = {};

        if (!values.email) {
            errors.email = t("login.requiredEmail");
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
            errors.email = t("login.invalidEmail");
        }

        if (!values.password) {
            errors.password = t("login.requiredPassword");
        } else if (values.password.length < 6) {
            errors.password = t("login.passwordValidation");
        }
        return errors;
    };

    const onSuccess = (res: any) => {
        if (!res?.token) {
            return;
        }
        setLoading(false);
        // Small delay to ensure Redux state is updated before navigation
        setTimeout(() => {
            if (res?.user?.roleId === USER_TYPES.SUPER_ADMIN) {
                navigate(ROUTE_NAMES.SUPER_ADMIN_ORGANIZATIONS);
            } else {
                navigate(ROUTE_NAMES.DASHBOARD);
            }
        }, 100);
    };

    const onError = () => {
        setLoading(false);
    };

    const onSubmit = (values: any) => {
        setLoading(true);
        try {
            const payload = {
                password: values.password,
                email: values.email,
            };
            dispatch(
                adminLogin({
                    payload,
                    onSuccess,
                    onError,
                })
            );
        } catch (error) { }
    };

    return (
        <Flex
            minH="100vh"
            width="100%"
            bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            align="center"
            justify="center"
            position="relative"
        >
            <Box position="relative">
                <Box position="absolute" top="0" right="0" zIndex={1000}>
                    <LanguageToggle variant="public" position="relative" />
                </Box>
                <AuthFormContainer minWidth={{ base: "auto", md: "550px" }}>
                    <AuthCardEllipse />

                    {/* Top Section */}
                    <AuthCardTopFrame>
                        <Flex align="center" gap="12.8px">
                            <KPSLogo />
                        </Flex>

                        <Flex direction="column" align="stretch" width="100%" gap="10px">
                            <AuthHeading>{t("superAdmin.portalTitle")}</AuthHeading>
                            <AuthSubheading>{t("superAdmin.portalSubtitle")}</AuthSubheading>
                        </Flex>
                    </AuthCardTopFrame>

                    {/* Form */}
                    <Formik
                        validate={validate}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                    >
                        {({ handleSubmit, errors, touched, values, setFieldValue }) => (
                            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                                <AuthFieldsWrapper>
                                    <Flex
                                        direction="column"
                                        align="flex-start"
                                        gap="1rem"
                                        width="100%"
                                    >
                                        {/* Email Input */}
                                        <Flex direction="column" gap="0.5rem" width="100%">
                                            <FloatingInput
                                                id="email"
                                                label={t("admin.userId")}
                                                name="email"
                                                value={values.email}
                                                error={errors.email}
                                                touched={touched.email}
                                                onChange={(e) => setFieldValue("email", e.target.value)}
                                            />
                                        </Flex>

                                        {/* Password */}
                                        <Flex direction="column" gap="0.5rem" width="100%">
                                            <FloatingInput
                                                id="password"
                                                label={t("login.password")}
                                                name="password"
                                                type="password"
                                                value={values.password}
                                                error={errors.password}
                                                touched={touched.password}
                                                onChange={(e) =>
                                                    setFieldValue("password", e.target.value)
                                                }
                                            />

                                            {/* Remember Me */}
                                            <Flex
                                                justify="space-between"
                                                align="center"
                                                width="100%"
                                                mt={4}
                                            >
                                                <Flex align="center" gap="0.35rem">
                                                    <Field
                                                        as={Checkbox}
                                                        id="rememberMe"
                                                        name="rememberMe"
                                                        iconSize="18px"
                                                        size="lg"
                                                        borderRadius="50%"
                                                        sx={{
                                                            ".chakra-checkbox__control": {
                                                                borderRadius: "50%",
                                                                width: "20px",
                                                                height: "20px",
                                                                border: "1px solid #000",
                                                            },
                                                            ".chakra-checkbox__control[data-checked]": {
                                                                backgroundColor: "#000",
                                                                borderColor: "#000",
                                                            },
                                                            ".chakra-checkbox__control[data-checked] svg": {
                                                                color: "#fff",
                                                            },
                                                        }}
                                                    />
                                                    <AuthCheckboxText>
                                                        {t("login.easyLogin")}
                                                    </AuthCheckboxText>
                                                    <InfoIcon />
                                                </Flex>
                                            </Flex>
                                        </Flex>
                                    </Flex>
                                </AuthFieldsWrapper>

                                {/* Button */}
                                <AuthButtonWrapper>
                                    <AuthButton
                                        isLoading={loading}
                                        isDisabled={!values.email || !values.password}
                                        bg={!values.email || !values.password ? "#EBEBEB" : "#667eea"}
                                        color={
                                            !values.email || !values.password ? "#BFBFBF" : "#FFFFFF"
                                        }
                                        _hover={{
                                            bg:
                                                !values.email || !values.password
                                                    ? "#dbd9d9ff"
                                                    : "#5568d3",
                                        }}
                                        type="submit"
                                    >
                                        {t("superAdmin.signInButton")}
                                    </AuthButton>
                                </AuthButtonWrapper>
                            </form>
                        )}
                    </Formik>
                </AuthFormContainer>
            </Box>
        </Flex>
    );
};
